/**
 * Lista de artigos (linhas com avatar do autor, título teal, excerpt e data)
 * usada na página da pasta e nos resultados de busca de /knowledge.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Pencil, Trash2 } from 'lucide-react';
import type { KnowledgeListItem } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/misc';
import { errorMessage } from '@/features/knowledge/knowledge-queries';
import { useDeleteKnowledge } from '@/features/knowledge/knowledge-mutations';
import { formatDateTime, initialOf } from '@/features/knowledge/knowledge-format';

function ArticleRow({
  item,
  onDelete,
}: {
  item: KnowledgeListItem;
  onDelete: (item: KnowledgeListItem) => void;
}) {
  return (
    <div className="group flex items-start gap-3 rounded-lg border bg-card p-4 shadow-xs">
      {/* Avatar inicial do autor (círculo teal claro) */}
      <span
        aria-hidden
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground"
      >
        {initialOf(item.authorName)}
      </span>

      <div className="min-w-0 flex-1">
        <Link
          to="/knowledge/$knowledgeId"
          params={{ knowledgeId: String(item.id) }}
          className="text-[15px] font-semibold text-primary transition-colors hover:text-primary-pressed"
        >
          {item.title}
        </Link>
        <div className="text-sm text-muted-foreground">Por {item.authorName ?? '—'}</div>
        {item.excerpt ? (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.excerpt}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-[13px] whitespace-nowrap text-muted-foreground tabular-nums">
          {formatDateTime(item.updatedAt)}
        </span>
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Link
            to="/knowledge/$knowledgeId/edit"
            params={{ knowledgeId: String(item.id) }}
            aria-label={`Editar ${item.title}`}
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
          >
            <Pencil className="size-4" />
          </Link>
          <button
            type="button"
            onClick={() => onDelete(item)}
            aria-label={`Excluir ${item.title}`}
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function KnowledgeArticleList({ items }: { items: KnowledgeListItem[] }) {
  const [toDelete, setToDelete] = useState<KnowledgeListItem | null>(null);
  const remove = useDeleteKnowledge();

  const confirmDelete = () => {
    if (!toDelete) return;
    remove.mutate(toDelete.id, { onSuccess: () => setToDelete(null) });
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <ArticleRow key={item.id} item={item} onDelete={setToDelete} />
        ))}
      </div>

      <Dialog
        open={toDelete !== null}
        onClose={() => {
          setToDelete(null);
          remove.reset();
        }}
        title="Excluir conhecimento"
      >
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir <strong className="text-foreground">{toDelete?.title}</strong>?
          Essa ação não pode ser desfeita.
        </p>
        {remove.error ? (
          <p className="mt-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setToDelete(null);
              remove.reset();
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={confirmDelete}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
